/**
 * Chart specs emitted by Claude inside ```chart fenced code blocks.
 * Shared between rm-portal and rm-portal-beta — pure JSON validation,
 * no framework deps.
 *
 * Deliberately flat/minimal. Claude is much more reliable when the schema is
 * small and every field has an obvious purpose — rich chart-library configs
 * (datasets, options trees, scales objects) invite hallucination. Each
 * portal's own chart components map these into chart.js configs.
 */

/**
 * Parse and validate a chart spec string. Returns null if the input is
 * unparseable or doesn't match a supported shape — callers show a friendly
 * error placeholder instead of crashing.
 */
export function parseChartSpec(raw) {
    if (typeof raw !== 'string' || raw.trim().length === 0) return null;

    let parsed;
    try {
        parsed = JSON.parse(raw);
    } catch {
        return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;
    const type = parsed.type;

    if (type === 'bar') {
        if (!Array.isArray(parsed.data)) return null;
        const data = parsed.data
            .filter((d) => d && typeof d.label === 'string' && typeof d.value === 'number')
            .map((d) => ({ label: d.label, value: d.value }));
        if (data.length === 0) return null;
        return {
            type: 'bar',
            title: typeof parsed.title === 'string' ? parsed.title : undefined,
            x_label: typeof parsed.x_label === 'string' ? parsed.x_label : undefined,
            y_label: typeof parsed.y_label === 'string' ? parsed.y_label : undefined,
            orientation: parsed.orientation === 'horizontal' ? 'horizontal' : 'vertical',
            data,
        };
    }

    if (type === 'pie' || type === 'donut') {
        if (!Array.isArray(parsed.data)) return null;
        const data = parsed.data
            .filter((d) => d && typeof d.label === 'string' && typeof d.value === 'number')
            .map((d) => ({ label: d.label, value: d.value }));
        if (data.length === 0) return null;
        return {
            type,
            title: typeof parsed.title === 'string' ? parsed.title : undefined,
            data,
        };
    }

    if (type === 'comparison') {
        if (!Array.isArray(parsed.subjects) || !Array.isArray(parsed.attributes)) return null;
        const subjects = parsed.subjects.filter((s) => typeof s === 'string');
        if (subjects.length === 0) return null;
        // Each attribute must have exactly one value per subject AND at least
        // one non-empty cell, otherwise we'd render a header with blank rows —
        // dropping the chart lets Claude's prose stand in its place instead.
        const rawAttributes = parsed.attributes
            .filter((a) => a && typeof a.label === 'string' && Array.isArray(a.values))
            .map((a) => ({
                label: a.label,
                values: a.values.map((v) => (v == null ? '' : String(v))),
            }));
        const attributes = rawAttributes.filter(
            (a) =>
                a.values.length === subjects.length &&
                a.values.some((v) => v.trim().length > 0),
        );
        if (attributes.length === 0) {
            // Surface what Claude emitted so we can tighten the prompt when
            // this keeps happening. Console-only — no user-visible noise.
            if (typeof console !== 'undefined') {
                console.warn(
                    '[chartSpec] comparison rejected — empty/mismatched values',
                    { subjects, rawAttributes },
                );
            }
            return null;
        }
        // Warn (but keep) if more than half of cells are blank — the table
        // will be mostly empty, which usually means Claude couldn't find the
        // data but chose to emit scaffolding anyway.
        const totalCells = attributes.length * subjects.length;
        const blankCells = attributes.reduce(
            (n, a) => n + a.values.filter((v) => v.trim().length === 0).length,
            0,
        );
        if (blankCells / totalCells > 0.5 && typeof console !== 'undefined') {
            console.warn(
                `[chartSpec] comparison mostly empty (${blankCells}/${totalCells} cells blank)`,
                { subjects, attributes },
            );
        }
        return {
            type: 'comparison',
            title: typeof parsed.title === 'string' ? parsed.title : undefined,
            subjects,
            attributes,
        };
    }

    return null;
}

/**
 * Chart colours — picked to be readable against both light and dark bubble
 * backgrounds. Cycles through as needed.
 */
export const CHART_COLORS = [
    'rgba(99, 102, 241, 0.75)',
    'rgba(16, 185, 129, 0.75)',
    'rgba(245, 158, 11, 0.75)',
    'rgba(236, 72, 153, 0.75)',
    'rgba(14, 165, 233, 0.75)',
    'rgba(168, 85, 247, 0.75)',
    'rgba(239, 68, 68, 0.75)',
    'rgba(132, 204, 22, 0.75)',
];
